import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, LoadingBlock } from "@/components/common/States";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import BillingPanel from "@/components/common/BillingPanel";
import SsoSettings from "@/components/common/SsoSettings";
import PrivacyAndSecurity from "@/components/common/PrivacyAndSecurity";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

const ROLES = ["admin", "editor", "approver", "viewer"];

export default function Settings() {
  const { role, refresh } = useAuth();
  const canAdmin = can(role, "tenant.settings");
  const canInvite = can(role, "tenant.invite");
  const [tenant, setTenant] = useState(null);
  const [plan, setPlan] = useState(null);
  const [members, setMembers] = useState(null);
  const [invites, setInvites] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invForm, setInvForm] = useState({ email: "", name: "", role: "editor" });

  const load = async () => {
    const [t, m] = await Promise.all([api.get("/tenants/me"), api.get("/tenants/members")]);
    setTenant(t.data.tenant); setPlan(t.data.plan); setMembers(m.data.items);
    setNameDraft(t.data.tenant.name);
    if (canInvite) {
      try { const { data } = await api.get("/auth/invites"); setInvites(data.items); } catch {}
    }
  };
  // load() reads state this effect deliberately runs without: it fetches
  // once on mount, and every later refresh is an explicit load() call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function saveTenant() {
    try {
      await api.patch("/tenants/me", { name: nameDraft });
      toast.success("Saved"); load(); refresh();
    } catch (e) { toast.error(humanError(e)); }
  }
  async function toggleApproval(val) {
    try {
      await api.patch("/tenants/me", { approval_required: val });
      toast.success(val ? "Approvals turned on" : "Approvals turned off");
      load();
    } catch (e) { toast.error(humanError(e)); }
  }
  async function changeRole(userId, newRole) {
    try { await api.patch(`/tenants/members/${userId}`, { role: newRole }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }
  async function removeMember(userId) {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/tenants/members/${userId}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }
  async function revokeInvite(invite) {
    // Worth confirming: the link already went out, and revoking makes it dead
    // rather than pending.
    if (!window.confirm(`Cancel the invitation to ${invite.email}? Their link will stop working.`)) return;
    try {
      await api.delete(`/auth/invites/${invite.id}`);
      toast.success(`Invitation to ${invite.email} cancelled`);
      load();
    } catch (e) { toast.error(humanError(e)); }
  }
  async function sendInvite() {
    try {
      const { data } = await api.post("/auth/invites", invForm);
      toast.success("Invite created", { description: `Token: ${data.token.slice(0, 12)}…` });
      navigator.clipboard?.writeText(`${window.location.origin}/invite?token=${data.token}`).catch(() => {});
      setInviteOpen(false); setInvForm({ email: "", name: "", role: "editor" });
      load();
    } catch (e) { toast.error(humanError(e)); }
  }

  if (!tenant) return <div><PageHeader title="Settings" /><LoadingBlock rows={5} /></div>;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Workspace, team, and plan." />

      <section className="mb-8 rounded-xl border border-[#E4E9EF] bg-white p-5">
        <div className="mb-4 text-sm font-semibold text-[#0B2B33]">Workspace</div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Name</label>
            <input
              data-testid="settings-tenant-name"
              value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} disabled={!canAdmin}
              className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm disabled:bg-[#F6F8FA]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Slug</label>
            <input value={tenant.slug} disabled className="w-full rounded-lg border border-[#E4E9EF] bg-[#F6F8FA] px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg bg-[#F6F8FA] p-3">
          <div>
            <div className="text-sm font-semibold text-[#0B2B33]">Require approval before publishing</div>
            <div className="text-xs text-[#43535E]">Turn on to send every submitted post through an approver.</div>
          </div>
          <Switch
            data-testid="approval-required-switch"
            checked={!!tenant.approval_required} onCheckedChange={toggleApproval} disabled={!canAdmin}
          />
        </div>
        {canAdmin ? (
          <div className="mt-4">
            <button data-testid="settings-save" onClick={saveTenant} className="hs-btn-primary text-sm">Save</button>
          </div>
        ) : null}
      </section>

      <section className="mb-8 rounded-xl border border-[#E4E9EF] bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-[#0B2B33]">Plan</div>
          {plan ? <span className="hs-chip">{plan.name}</span> : null}
        </div>
        {plan ? (
          <div className="grid gap-4 md:grid-cols-4 text-sm">
            <Meta label="Seats" value={plan.max_seats} />
            <Meta label="Social accounts" value={plan.max_social_accounts} />
            <Meta label="Scheduled posts" value={plan.max_scheduled_posts.toLocaleString()} />
            <Meta label="Monthly price" value={`$${(plan.price_monthly / 100).toFixed(0)}`} />
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-[#E4E9EF] bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-[#0B2B33]">Team</div>
          {canInvite ? (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <button data-testid="invite-open" className="hs-btn-primary text-sm">Invite teammate</button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite a teammate</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Email</label>
                    <input data-testid="invite-email" value={invForm.email} onChange={(e) => setInvForm({ ...invForm, email: e.target.value })} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Name</label>
                    <input data-testid="invite-name" value={invForm.name} onChange={(e) => setInvForm({ ...invForm, name: e.target.value })} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Role</label>
                    <select data-testid="invite-role" value={invForm.role} onChange={(e) => setInvForm({ ...invForm, role: e.target.value })} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm capitalize">
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <DialogFooter>
                  <button onClick={() => setInviteOpen(false)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm">Cancel</button>
                  <button data-testid="invite-send" onClick={sendInvite} className="hs-btn-primary text-sm">Send invite</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
        </div>
        {members === null ? <LoadingBlock rows={3} /> :
          members.length === 0 ? <EmptyState title="Just you here" /> :
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
                  <th className="pb-2">Person</th><th className="pb-2">Email</th><th className="pb-2">Role</th><th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4E9EF]">
                {members.map(m => (
                  <tr key={m.user_id}>
                    <td className="py-2 text-[#0B2B33]">{m.name}</td>
                    <td className="py-2 text-[#43535E]">{m.email}</td>
                    <td className="py-2">
                      {canInvite ? (
                        <select
                          data-testid={`member-role-${m.user_id}`}
                          value={m.role}
                          onChange={(e) => changeRole(m.user_id, e.target.value)}
                          className="rounded-lg border border-[#E4E9EF] px-2 py-1 text-xs capitalize"
                        >
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : <span className="text-xs capitalize">{m.role}</span>}
                    </td>
                    <td className="py-2 text-right">
                      {canInvite ? <button data-testid={`member-remove-${m.user_id}`} onClick={() => removeMember(m.user_id)} className="text-xs font-semibold text-[#E3252A] hover:underline">Remove</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }

        {invites && invites.length ? (
          <>
            <div className="mt-6 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Pending invites</div>
            <ul className="mt-2 divide-y divide-[#E4E9EF]">
              {invites.map(i => (
                <li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>{i.email} <span className="text-xs text-[#8A97A0]">· {i.role}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="hs-chip">{i.used ? "Used" : "Pending"}</span>
                    {i.used ? null : (
                      <button
                        type="button"
                        onClick={() => revokeInvite(i)}
                        data-testid={`revoke-invite-${i.id}`}
                        className="rounded-full border border-[#E4E9EF] px-3 py-1 text-xs font-semibold text-[#B02328] hover:bg-[#FDECEC]"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>

      <div className="space-y-8">
        <BillingPanel />
        <SsoSettings />
        <PrivacyAndSecurity />
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F6F8FA] p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">{label}</div>
      <div className="mt-1 font-display text-lg font-semibold text-[#0B2B33]">{value}</div>
    </div>
  );
}
